<template>
  <div class="member-type-select">
    <el-card class="select-card">
      <template #header>
        <div class="card-header">
          <h2>选择会员类型</h2>
          <p>请根据您的需求选择合适的会员类型</p>
        </div>
      </template>

      <MemberTypeSelector v-model="selectedType" v-model:level="selectedLevel" @change="handleTypeChange" @permissions-change="handlePermissionsChange" />

      <div class="action-buttons">
        <el-button size="large" @click="goBack">
          返回
        </el-button>
        <el-button type="primary" size="large" :disabled="!selectedType" @click="handleNext">
          下一步
        </el-button>
      </div>
    </el-card>

    <!-- 会员信息确认弹窗 -->
    <el-dialog
      v-model="confirmDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      :show-close="true"
      custom-class="confirm-dialog"
    >
      <template #header>
        <div class="confirm-header">
          <div class="header-logo">
            <div class="logo-icon">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
              </svg>
            </div>
          </div>
          <h3 class="confirm-title">会员信息确认</h3>
          <p class="confirm-org">山东心理咨询行业协会</p>
          <div class="header-divider"></div>
        </div>
      </template>

      <div class="confirm-body">
        <!-- 会员信息表格 -->
        <div class="confirm-info-table">
          <div class="table-row">
            <div class="table-label">会员类型</div>
            <div class="table-value">{{ confirmTypeName }}</div>
          </div>
          <div class="table-row" v-if="selectedType === MEMBER_TYPE.INDIVIDUAL">
            <div class="table-label">会员等级</div>
            <div class="table-value table-value--primary">{{ confirmLevelName }}</div>
          </div>
          <div class="table-row">
            <div class="table-label">缴费年份</div>
            <div class="table-value">{{ currentYear }} 年度</div>
          </div>
          <div class="table-row table-row--highlight">
            <div class="table-label">应缴金额</div>
            <div class="table-amount" v-if="confirmFee !== null">
              <span class="amount-currency">人民币</span>
              <span class="amount-symbol">¥</span>
              <span class="amount-number">{{ confirmFee }}</span>
              <span class="amount-unit">元</span>
            </div>
            <div class="table-amount table-amount--negotiate" v-else>
              <span>面议</span>
            </div>
          </div>
        </div>

        <!-- 会员权益 -->
        <div class="confirm-benefits" v-if="permissions.length">
          <div class="benefits-title">
            <span class="title-text">会员权益</span>
            <span class="title-line"></span>
          </div>
          <div class="benefits-grid">
            <div class="benefit-item" v-for="(item, index) in permissions" :key="index">
              <div class="benefit-icon">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </div>
              <span class="benefit-text">{{ item }}</span>
            </div>
          </div>
        </div>

        <!-- 重要提示 -->
        <div class="confirm-notice">
          <div class="notice-icon">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
            </svg>
          </div>
          <div class="notice-content">
            <div class="notice-title">重要提示</div>
            <div class="notice-text">请先完善会员信息并提交审核，审核通过后方可进行缴费。</div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="confirm-footer">
          <el-button class="footer-btn footer-btn--cancel" size="large" @click="confirmDialogVisible = false">
            暂不办理
          </el-button>
          <el-button class="footer-btn footer-btn--primary" type="primary" size="large" @click="openMemberInfoDialog">
            立即完善信息
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 完善信息弹窗 -->
    <el-dialog 
      v-model="memberInfoDialogVisible" 
      width="900px" 
      :close-on-click-modal="false"
      :show-close="true"
      custom-class="member-info-dialog">
      <template #header>
        <div class="info-dialog-header">
          <div class="header-logo">
            <div class="logo-icon">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
              </svg>
            </div>
          </div>
          <h3 class="info-dialog-title">{{ isEditMode ? '编辑会员信息' : '完善会员信息' }}</h3>
          <p class="info-dialog-org">山东心理咨询行业协会</p>
          <div class="header-divider"></div>
        </div>
      </template>
      
      <el-form ref="memberInfoFormRef" :model="memberInfoForm" :rules="memberInfoRules" label-width="140px" size="default" class="member-info-form">
        <!-- 基本信息 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="真实姓名" prop="realName">
              <el-input v-model="memberInfoForm.realName" placeholder="请输入真实姓名"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="手机" prop="phone">
              <el-input v-model="memberInfoForm.phone" placeholder="请输入手机号" maxlength="11" show-word-limit></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="电子邮箱" prop="email">
              <el-input v-model="memberInfoForm.email" placeholder="请输入电子邮箱"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="出生日期" prop="birthday">
              <el-date-picker v-model="memberInfoForm.birthday" type="date" placeholder="请选择出生日期"
                format="YYYY-MM-DD" value-format="YYYY-MM-DD" style="width: 100%"></el-date-picker>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="任职单位" prop="company">
              <el-input v-model="memberInfoForm.company" placeholder="请输入任职单位"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="职称" prop="title">
              <el-input v-model="memberInfoForm.title" placeholder="请输入职称"></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="最终学历" prop="education">
              <el-radio-group v-model="memberInfoForm.education">
                <el-radio label="大专">大专</el-radio>
                <el-radio label="本科">本科</el-radio>
                <el-radio label="硕士">硕士</el-radio>
                <el-radio label="博士">博士</el-radio>
                <el-radio label="其他">其他</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="学位证书号" prop="degreeNo">
              <el-input v-model="memberInfoForm.degreeNo" placeholder="请输入学位证书号"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="毕业院校" prop="graduateSchool">
              <el-input v-model="memberInfoForm.graduateSchool" placeholder="请输入毕业院校"></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="专业" prop="major">
              <el-input v-model="memberInfoForm.major" placeholder="请输入专业"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="从事心理咨询的起始时间" prop="startDate">
              <el-date-picker v-model="memberInfoForm.startDate" type="date" placeholder="请选择起始时间"
                format="YYYY-MM-DD" value-format="YYYY-MM-DD" style="width: 100%"></el-date-picker>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 见习/实习经历 -->
        <el-divider content-position="left">见习/实习经历</el-divider>
        <div v-for="(exp, index) in memberInfoForm.experiences" :key="index" class="experience-item">
          <div class="exp-header">
            <span class="exp-title">第 {{ index === 0 ? '一' : index === 1 ? '二' : index + 1 }} 段经历</span>
            <el-button v-if="index > 0" type="danger" link size="small" @click="removeExperience(index)">删除</el-button>
          </div>

          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="时间" :prop="'experiences.' + index + '.time'">
                <el-date-picker v-model="exp.time" type="daterange" range-separator="至"
                  start-placeholder="开始日期" end-placeholder="结束日期" format="YYYY-MM-DD"
                  value-format="YYYY-MM-DD" style="width: 100%"></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="见习/实习地点" :prop="'experiences.' + index + '.location'">
                <el-input v-model="exp.location" placeholder="见习/实习地点"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="督导老师姓名" :prop="'experiences.' + index + '.supervisorName'">
                <el-input v-model="exp.supervisorName" placeholder="输入老师姓名"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="接受督导小时数" :prop="'experiences.' + index + '.supervisorHours'">
                <el-input v-model="exp.supervisorHours" placeholder="输入小时数" type="number"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <div class="add-experience-btn-wrapper">
          <el-button type="primary" plain @click="addExperience">
            <el-icon style="margin-right: 6px;"><Plus /></el-icon>
            点此可继续增加新的经历
          </el-button>
        </div>

        <!-- 其他信息 -->
        <el-divider content-position="left">其他信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="此前是否已是我协会的会员" prop="isMember">
              <el-radio-group v-model="memberInfoForm.isMember">
                <el-radio label="是">是</el-radio>
                <el-radio label="否">否</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="是否有违规记录" prop="hasViolation">
              <el-radio-group v-model="memberInfoForm.hasViolation">
                <el-radio label="是">是</el-radio>
                <el-radio label="否">否</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="服务人次" prop="serviceCount">
              <el-input v-model="memberInfoForm.serviceCount" placeholder="服务人次" type="number"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="督导个案数" prop="caseCount">
              <el-input v-model="memberInfoForm.caseCount" placeholder="督导个案数" type="number"></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row>
          <el-col :span="24">
            <el-form-item label="其它补充说明" prop="remark">
              <el-input v-model="memberInfoForm.remark" type="textarea" :rows="3" placeholder="请输入内容"></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 文件上传 -->
        <el-divider content-position="left">附件上传</el-divider>
        <div class="upload-section">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="个人照片" prop="photoUrl">
                <div class="upload-item">
                  <el-upload
                    :action="upload_url"
                    :headers="uploadHeaders"
                    :limit="1"
                    :on-success="handlePhotoSuccess"
                    :on-error="handleUploadError"
                    :before-upload="beforeUpload"
                    :show-file-list="false"
                    accept="image/jpeg,image/jpg,image/png">
                    <div class="upload-box upload-box--id-photo">
                      <el-image v-if="memberInfoForm.photoUrl" class="upload-preview" :src="memberInfoForm.photoUrl" fit="cover" />
                      <div v-else class="upload-placeholder">
                        <el-icon size="24"><Plus /></el-icon>
                        <span>上传照片</span>
                      </div>
                    </div>
                  </el-upload>
                  <div class="upload-tip">一寸证件照（25×35mm）</div>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="资格证书" prop="certificateUrl">
                <div class="upload-item">
                  <el-upload
                    :action="upload_url"
                    :headers="uploadHeaders"
                    :limit="1"
                    :on-success="handleCertificateSuccess"
                    :on-error="handleUploadError"
                    :before-upload="beforeUpload"
                    :show-file-list="false"
                    accept="image/jpeg,image/jpg,image/png">
                    <div class="upload-box">
                      <el-image v-if="memberInfoForm.certificateUrl" class="upload-preview" :src="memberInfoForm.certificateUrl" fit="cover" />
                      <div v-else class="upload-placeholder">
                        <el-icon size="24"><Plus /></el-icon>
                        <span>上传证书</span>
                      </div>
                    </div>
                  </el-upload>
                  <div class="upload-tip">心理咨询师资格证书</div>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="学位证书" prop="degreeUrl">
                <div class="upload-item">
                  <el-upload
                    :action="upload_url"
                    :headers="uploadHeaders"
                    :limit="1"
                    :on-success="handleDegreeSuccess"
                    :on-error="handleUploadError"
                    :before-upload="beforeUpload"
                    :show-file-list="false"
                    accept="image/jpeg,image/jpg,image/png">
                    <div class="upload-box">
                      <el-image v-if="memberInfoForm.degreeUrl" class="upload-preview" :src="memberInfoForm.degreeUrl" fit="cover" />
                      <div v-else class="upload-placeholder">
                        <el-icon size="24"><Plus /></el-icon>
                        <span>上传证书</span>
                      </div>
                    </div>
                  </el-upload>
                  <div class="upload-tip">学位证书扫描件</div>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

      </el-form>

      <template #footer>
        <div class="info-dialog-footer">
          <el-button class="footer-btn footer-btn--cancel" size="large" @click="memberInfoDialogVisible = false">
            取消
          </el-button>
          <el-button 
            class="footer-btn footer-btn--primary" 
            type="primary" 
            size="large"
            :loading="submittingMemberInfo" 
            @click="submitMemberInfo">
            {{ submittingMemberInfo ? '提交中...' : '立即提交' }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 支付方式选择弹窗 -->
    <el-dialog
      v-model="paymentDialogVisible"
      title="选择支付方式"
      :width="selectedPaymentMethod === 'wechat' ? '700px' : '400px'"
      :close-on-click-modal="false"
    >
      <div class="payment-container" :class="{ 'wechat-active': selectedPaymentMethod === 'wechat' }">
        <div class="payment-methods">
          <div
            class="payment-item"
            :class="{ active: selectedPaymentMethod === 'wechat' }"
            @click="selectPaymentMethod('wechat')"
          >
            <img src="@/assets/images/weixin.png" alt="微信" class="payment-icon" />
            <span class="payment-name">微信支付</span>
            <i class="el-icon-check check-icon" v-if="selectedPaymentMethod === 'wechat'"></i>
          </div>
          <div
            class="payment-item"
            :class="{ active: selectedPaymentMethod === 'alipay' }"
            @click="selectPaymentMethod('alipay')"
          >
            <img src="@/assets/images/alipay.png" alt="支付宝" class="payment-icon" />
            <span class="payment-name">支付宝支付</span>
            <i class="el-icon-check check-icon" v-if="selectedPaymentMethod === 'alipay'"></i>
          </div>
          <div class="alipay-tip" v-if="selectedPaymentMethod === 'alipay'">
            <el-icon><InfoFilled /></el-icon>
            <span>点击下方按钮将跳转至支付宝收银台完成支付</span>
          </div>
        </div>

        <div class="qrcode-section" v-if="selectedPaymentMethod === 'wechat'">
          <div class="qrcode-header">
            <img src="@/assets/images/weixin.png" alt="微信" class="payment-logo" />
            <span class="payment-title">微信扫码支付</span>
          </div>
          <div class="qrcode-wrapper">
            <div v-if="qrcodeUrl" :key="qrcodeKey" ref="qrcodeRef" class="qrcode-canvas"></div>
            <div v-else class="qrcode-loading">
              <i class="el-icon-loading"></i>
              <p>二维码生成中...</p>
            </div>
          </div>
          <div class="qrcode-tips">
            <p>请使用微信扫描二维码完成支付</p>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="paymentDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmPayment" :disabled="!selectedPaymentMethod">
            {{ selectedPaymentMethod === 'alipay' ? '前往支付宝支付' : '已完成支付' }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import MemberTypeSelector from '@/components/MemberTypeSelector.vue'
import { MEMBER_TYPE, MEMBER_TYPE_NAMES, MEMBER_LEVEL_NAMES } from '@/utils/constants'
import { Plus, InfoFilled } from '@element-plus/icons-vue'
import api from '@/api/index'
import attachment from '@/api/public'
import memberApi from '@/api/member'
import QRCode from 'qrcodejs2-fix'

export default {
  name: 'MemberTypeSelect',

  components: {
    MemberTypeSelector,
    Plus,
    InfoFilled
  },

  data() {
    return {
      MEMBER_TYPE,
      selectedType: null,
      selectedLevel: null,
      permissions: [],
      confirmDialogVisible: false,
      confirmFee: null,
      memberInfoDialogVisible: false,
      isEditMode: false,
      paymentDialogVisible: false,
      selectedPaymentMethod: null,
      qrcodeUrl: '',
      qrcodeKey: 0,
      upload_url: '',
      memberInfoForm: {
        realName: '',
        phone: '',
        email: '',
        birthday: '',
        company: '',
        title: '',
        education: '本科',
        degreeNo: '',
        graduateSchool: '',
        major: '',
        startDate: '',
        experiences: [
          {
            time: [],
            location: '',
            supervisorName: '',
            supervisorHours: ''
          },
          {
            time: [],
            location: '',
            supervisorName: '',
            supervisorHours: ''
          }
        ],
        isMember: '否',
        hasViolation: '否',
        serviceCount: '',
        caseCount: '',
        remark: '',
        photoUrl: '',
        certificateUrl: '',
        degreeUrl: ''
      },
      memberInfoRules: {
        realName: [
          { required: true, message: '请输入真实姓名', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        email: [
          { required: true, message: '请输入电子邮箱', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
        ],
        birthday: [
          { required: true, message: '请选择出生日期', trigger: 'change' }
        ],
        company: [
          { required: true, message: '请输入任职单位', trigger: 'blur' }
        ],
        education: [
          { required: true, message: '请选择最终学历', trigger: 'change' }
        ]
      },
      submittingMemberInfo: false
    }
  },

  computed: {
    ...mapGetters('auth', ['memberInfo', 'token']),
    currentYear() {
      return new Date().getFullYear()
    },
    confirmTypeName() {
      return MEMBER_TYPE_NAMES[this.selectedType] || ''
    },
    confirmLevelName() {
      return MEMBER_LEVEL_NAMES[this.selectedLevel] || ''
    },
    uploadHeaders() {
      return {
        'Authorization': this.token || 'no-token'
      }
    }
  },

  mounted() {
    // 如果已有会员信息，预选择
    if (this.memberInfo && this.memberInfo.member_type) {
      this.selectedType = this.memberInfo.member_type
      this.selectedLevel = this.memberInfo.member_level
    }
    
    // 获取上传地址
    attachment.uploadFileUrl().then(res => {
      this.upload_url = res
    })
  },

  methods: {
    /**
     * 处理类型变化
     */
    handleTypeChange() {
      // 会员类型已选择
    },

    /**
     * 处理权益列表变化
     */
    handlePermissionsChange(list) {
      this.permissions = list
    },

    /**
     * 下一步 - 存储会员类型和等级，引导用户去完善会员信息
     */
    async handleNext() {
      if (!this.selectedType) {
        this.$message.warning('请选择会员类型')
        return
      }

      // 个人会员需要选择等级
      if (this.selectedType === MEMBER_TYPE.INDIVIDUAL && !this.selectedLevel) {
        this.$message.warning('请选择会员等级')
        return
      }

      // 存储会员类型和等级到本地
      localStorage.setItem('pending_member_type', this.selectedType)
      if (this.selectedLevel) {
        localStorage.setItem('pending_member_level', this.selectedLevel)
      }
      
      // 清除之前的完善状态
      localStorage.removeItem('member_info_completed')

      // 获取费用信息并显示确认弹窗
      this.confirmFee = null
      if (this.selectedType === MEMBER_TYPE.INDIVIDUAL) {
        try {
          const res = await api.getFeeConfig({
            member_type: this.selectedType,
            member_level: this.selectedLevel
          })
          if (res.code === 2000 && res.data && res.data.specific_config) {
            this.confirmFee = res.data.specific_config.fee
          }
        } catch (e) {
          console.error('获取会费配置失败:', e)
        }
      }

      // 打开确认弹窗
      this.confirmDialogVisible = true
    },

    /**
     * 去完善信息 - 打开完善信息弹窗，先查询是否已有会员信息
     */
    async openMemberInfoDialog() {
      this.confirmDialogVisible = false
      this.isEditMode = false

      try {
        const res = await memberApi.getMemberInfo()
        if (res && res.code === 2000 && res.data && res.data.id) {
          // 已有会员信息，进入编辑模式并回填表单
          this.isEditMode = true
          const d = res.data
          let experiences = []
          try {
            experiences = d.experiences ? JSON.parse(d.experiences) : []
          } catch (e) {
            experiences = []
          }
          if (!experiences.length) {
            experiences = [
              { time: [], location: '', supervisorName: '', supervisorHours: '' },
              { time: [], location: '', supervisorName: '', supervisorHours: '' }
            ]
          }
          this.memberInfoForm = {
            realName: d.name || '',
            phone: d.phone || '',
            email: d.email || '',
            birthday: d.birthday || '',
            company: d.company || '',
            title: d.title || '',
            education: d.education || '本科',
            degreeNo: d.degree_no || '',
            graduateSchool: d.graduate_school || '',
            major: d.major || '',
            startDate: d.start_date || '',
            experiences,
            isMember: d.is_member || '否',
            hasViolation: d.has_violation || '否',
            serviceCount: d.service_count || '',
            caseCount: d.case_count || '',
            remark: d.remark || '',
            photoUrl: d.photo || '',
            certificateUrl: d.certificate || '',
            degreeUrl: d.degree_certificate || ''
          }
        }
      } catch (e) {
        console.error('获取会员信息失败:', e)
      }

      this.memberInfoDialogVisible = true
    },

    /**
     * 添加经历
     */
    addExperience() {
      this.memberInfoForm.experiences.push({
        time: [],
        location: '',
        supervisorName: '',
        supervisorHours: ''
      })
    },

    /**
     * 删除经历
     */
    removeExperience(index) {
      this.memberInfoForm.experiences.splice(index, 1)
    },

    /**
     * 上传前校验
     */
    beforeUpload(file) {
      const isImage = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg'
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isImage) {
        this.$message.error('只能上传 JPG/PNG 格式的图片！')
        return false
      }
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB！')
        return false
      }
      return true
    },

    /**
     * 上传成功 - 个人照片
     */
    handlePhotoSuccess(res) {
      if (res.code == 4000) {
        this.$message.error(res.msg)
      } else {
        this.memberInfoForm.photoUrl = res.data.url
        this.$message.success('上传成功！')
      }
    },

    /**
     * 上传成功 - 资格证书
     */
    handleCertificateSuccess(res) {
      if (res.code == 4000) {
        this.$message.error(res.msg)
      } else {
        this.memberInfoForm.certificateUrl = res.data.url
        this.$message.success('上传成功！')
      }
    },

    /**
     * 上传成功 - 学位证书
     */
    handleDegreeSuccess(res) {
      if (res.code == 4000) {
        this.$message.error(res.msg)
      } else {
        this.memberInfoForm.degreeUrl = res.data.url
        this.$message.success('上传成功！')
      }
    },

    /**
     * 上传失败
     */
    handleUploadError(err) {
      this.$message.error('上传失败，请重试！')
      console.error('上传错误：', err)
    },

    /**
     * 提交会员信息
     */
    async submitMemberInfo() {
      const valid = await this.$refs.memberInfoFormRef.validate().catch(() => false)
      if (!valid) {
        this.$message.error('请完善必填信息')
        return
      }

      this.submittingMemberInfo = true

      try {
        // 准备提交数据
        const submitData = {
          member_type: this.selectedType,
          member_level: this.selectedLevel,
          name: this.memberInfoForm.realName,
          phone: this.memberInfoForm.phone,
          email: this.memberInfoForm.email,
          birthday: this.memberInfoForm.birthday,
          company: this.memberInfoForm.company,
          title: this.memberInfoForm.title,
          education: this.memberInfoForm.education,
          degree_no: this.memberInfoForm.degreeNo,
          graduate_school: this.memberInfoForm.graduateSchool,
          major: this.memberInfoForm.major,
          start_date: this.memberInfoForm.startDate,
          experiences: JSON.stringify(this.memberInfoForm.experiences),
          has_violation: this.memberInfoForm.hasViolation,
          service_count: this.memberInfoForm.serviceCount ? Number(this.memberInfoForm.serviceCount) : 0,
          case_count: this.memberInfoForm.caseCount ? Number(this.memberInfoForm.caseCount) : null,
          remark: this.memberInfoForm.remark,
          photo: this.memberInfoForm.photoUrl,
          certificate: this.memberInfoForm.certificateUrl,
          degree_certificate: this.memberInfoForm.degreeUrl
        }

        const res = await (this.isEditMode
          ? memberApi.updateMemberInfo(submitData)
          : memberApi.createMember(submitData))

        if (res && res.code === 2000) {
          this.$message.success(res.msg || '提交成功！')
          this.memberInfoDialogVisible = false

          // 标记会员信息已完善
          localStorage.setItem('member_info_completed', 'true')

          // 刷新会员信息
          if (res.data) {
            await this.$store.dispatch('auth/refreshMemberInfo', res.data)
          }

          // 打开支付弹窗
          this.memberInfoDialogVisible = false
          this.paymentDialogVisible = true
          this.selectedPaymentMethod = null
          this.qrcodeUrl = ''
        }
      } catch (error) {
        console.error('提交失败:', error)
        this.$message.error('提交失败，请稍后重试')
      } finally {
        this.submittingMemberInfo = false
      }
    },

    /**
     * 选择支付方式
     */
    async selectPaymentMethod(method) {
      this.selectedPaymentMethod = method
      // 微信需要立即生成二维码，支付宝在点击按钮时再创建订单
      if (method === 'wechat') {
        await this.createPaymentOrder(method)
      }
    },

    /**
     * 创建支付订单
     */
    async createPaymentOrder(method) {
      try {
        this.qrcodeUrl = '' // 清空二维码
        
        const params = {
          payment_type: method === 'wechat' ? 1 : 2, // 支付类型：1-微信支付 2-支付宝支付
          payment_method: method === 'alipay' ? 'page' : 'native', // 支付方式：支付宝用page，微信用native
          // member_level: this.selectedLevel || 1, // 会员等级
          year: new Date().getFullYear(), // 当前年份
          return_url: 'https://sdxlxh.cn/payment-successful', // 支付完成跳转地址
          remark: `${this.selectedType === MEMBER_TYPE.INDIVIDUAL ? '个人' : '企业'}会员支付`
        }

        const res = await api.createPayment(params)
        
        if (res && res.code === 2000) {
          if (method === 'alipay') {
            // 支付宝：打开新窗口跳转到支付页面
            const payData = res.data.pay_data
            if (payData) {
              window.open(payData, '_blank')
              this.paymentDialogVisible = false // 关闭弹窗
              
              // 清除本地存储
              localStorage.removeItem('pending_member_type')
              localStorage.removeItem('pending_member_level')
              localStorage.removeItem('member_info_completed')
              
              // 跳转到首页
              this.$router.push('/user/home')
            } else {
              this.$message.error('获取支付链接失败')
            }
          } else {
            // 微信：使用 pay_data.code_url 生成二维码
            const codeUrl = res.data.pay_data?.code_url
            if (codeUrl) {
              this.qrcodeUrl = codeUrl
              this.$nextTick(() => {
                this.qrcodeKey++
                this.$nextTick(() => {
                  this.generateQRCode(codeUrl)
                })
              })
            } else {
              this.$message.error('获取支付二维码失败')
            }
          }
        } else {
          this.$message.error(res.msg || '创建支付订单失败')
        }
      } catch (error) {
        console.error('创建支付订单失败:', error)
        this.$message.error('创建支付订单失败，请稍后重试')
      }
    },

    /**
     * 生成二维码
     */
    generateQRCode(url) {
      const container = this.$refs.qrcodeRef
      if (!container) return
      // 清空旧二维码
      container.innerHTML = ''
      new QRCode(container, {
        text: url,
        width: 200,
        height: 200,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.H
      })
    },

    /**
     * 确认支付
     */
    async confirmPayment() {
      if (!this.selectedPaymentMethod) {
        this.$message.warning('请选择支付方式')
        return
      }

      // 支付宝：点击按钮时创建订单并跳转
      if (this.selectedPaymentMethod === 'alipay') {
        await this.createPaymentOrder('alipay')
        return
      }
      
      // 微信：已完成支付
      this.paymentDialogVisible = false
      
      // 清除本地存储
      localStorage.removeItem('pending_member_type')
      localStorage.removeItem('pending_member_level')
      localStorage.removeItem('member_info_completed')
      
      this.$message.success('支付确认成功，请稍后查看订单状态')
      
      // 跳转到首页
      this.$router.push('/user/home')
    },

    /**
     * 去完善信息 - 跳转到会员信息页面（保留作为备用）
     */
    goToMemberInfo() {
      this.confirmDialogVisible = false
      this.$router.push('/user/member-info')
    },

    /**
     * 返回
     */
    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style lang="scss" scoped>
.member-type-select {
  min-height: 100vh;
  padding: 40px 20px;
  background-color: #f5f7fa;

  .select-card {
    max-width: 1400px;
    margin: 0 auto;

    .card-header {
      text-align: center;

      h2 {
        margin: 0 0 10px 0;
        font-size: 28px;
        color: #303133;
      }

      p {
        margin: 0;
        color: #909399;
        font-size: 16px;
      }
    }

    .action-buttons {
      display: flex;
      justify-content: center;
      gap: 20px;
      margin-top: 30px;
      padding-top: 30px;
      border-top: 1px solid #e4e7ed;
    }
  }
}

.confirm-header {
  text-align: center;
  padding: 0;
  background: linear-gradient(180deg, #f8f9fc 0%, #ffffff 100%);
  border-bottom: 2px solid #1a3a6b;
  position: relative;

  .header-logo {
    padding: 28px 0 16px;

    .logo-icon {
      width: 72px;
      height: 72px;
      margin: 0 auto;
      background: linear-gradient(135deg, #1a3a6b 0%, #2c5282 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      box-shadow: 0 4px 16px rgba(26, 58, 107, 0.25);
      border: 3px solid #fff;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        inset: -6px;
        border-radius: 50%;
        border: 1px solid rgba(26, 58, 107, 0.15);
      }
    }
  }

  .confirm-title {
    margin: 0 0 8px;
    font-size: 24px;
    font-weight: 700;
    color: #1a3a6b;
    letter-spacing: 2px;
    font-family: 'Microsoft YaHei', 'SimHei', sans-serif;
  }

  .confirm-org {
    margin: 0 0 20px;
    font-size: 14px;
    color: #5a6c7d;
    letter-spacing: 3px;
    font-weight: 500;
  }

  .header-divider {
    height: 3px;
    background: linear-gradient(90deg, transparent 0%, #c8a96e 50%, transparent 100%);
    margin: 0 auto;
    width: 60%;
  }
}

.confirm-body {
  padding: 32px 0 24px;

  .confirm-info-table {
    background: #ffffff;
    border: 1px solid #e0e6ed;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 24px;

    .table-row {
      display: flex;
      align-items: center;
      border-bottom: 1px solid #f0f3f7;

      &:last-child {
        border-bottom: none;
      }

      &.table-row--highlight {
        background: linear-gradient(90deg, #fafbfc 0%, #f8f9fb 100%);
        border-top: 2px solid #e0e6ed;
        border-bottom: 2px solid #e0e6ed;
      }

      .table-label {
        flex: 0 0 120px;
        padding: 16px 20px;
        background: #f8f9fc;
        border-right: 1px solid #e0e6ed;
        font-size: 14px;
        color: #5a6c7d;
        font-weight: 600;
        letter-spacing: 0.5px;
      }

      .table-value {
        flex: 1;
        padding: 16px 20px;
        font-size: 15px;
        color: #2c3e50;
        font-weight: 500;

        &.table-value--primary {
          color: #1a3a6b;
          font-weight: 600;
        }
      }

      .table-amount {
        flex: 1;
        padding: 16px 20px;
        display: flex;
        align-items: baseline;
        gap: 4px;
        color: #c8342e;
        font-weight: 600;

        .amount-currency {
          font-size: 13px;
          color: #5a6c7d;
          margin-right: 4px;
        }

        .amount-symbol {
          font-size: 18px;
        }

        .amount-number {
          font-size: 32px;
          font-weight: 700;
          font-family: 'DIN', 'Arial', sans-serif;
          line-height: 1;
        }

        .amount-unit {
          font-size: 14px;
          color: #5a6c7d;
          margin-left: 2px;
        }

        &.table-amount--negotiate {
          font-size: 20px;
          color: #c8a96e;
          font-weight: 600;
        }
      }
    }
  }

  .confirm-benefits {
    margin-bottom: 24px;

    .benefits-title {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      gap: 12px;

      .title-text {
        font-size: 15px;
        color: #1a3a6b;
        font-weight: 600;
        letter-spacing: 1px;
        white-space: nowrap;
      }

      .title-line {
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, #c8a96e 0%, transparent 100%);
      }
    }

    .benefits-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;

      .benefit-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 14px;
        background: #f8f9fc;
        border: 1px solid #e8eef5;
        border-radius: 4px;
        transition: all 0.2s ease;

        &:hover {
          background: #f0f4f8;
          border-color: #c8a96e;
        }

        .benefit-icon {
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          background: #1a3a6b;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
        }

        .benefit-text {
          font-size: 13px;
          color: #2c3e50;
          line-height: 1.4;
        }
      }
    }
  }

  .confirm-notice {
    display: flex;
    gap: 12px;
    padding: 16px 18px;
    background: linear-gradient(135deg, #fffbf0 0%, #fff8e6 100%);
    border: 1px solid #f0e5c8;
    border-left: 4px solid #c8a96e;
    border-radius: 4px;

    .notice-icon {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      color: #c8a96e;
    }

    .notice-content {
      flex: 1;

      .notice-title {
        font-size: 14px;
        color: #8b7355;
        font-weight: 600;
        margin-bottom: 4px;
        letter-spacing: 0.5px;
      }

      .notice-text {
        font-size: 13px;
        color: #6b5d4f;
        line-height: 1.6;
      }
    }
  }
}

.confirm-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding-top: 8px;
  border-top: 1px solid #e8eef5;

  .footer-btn {
    min-width: 140px;
    height: 46px;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 1px;
    border-radius: 4px;
    transition: all 0.3s ease;

    &.footer-btn--cancel {
      border: 1px solid #d1d5db;
      color: #6b7280;
      background: #ffffff;

      &:hover {
        border-color: #9ca3af;
        color: #4b5563;
        background: #f9fafb;
      }
    }

    &.footer-btn--primary {
      background: linear-gradient(135deg, #1a3a6b 0%, #2c5282 100%);
      border: none;
      color: #ffffff;
      box-shadow: 0 4px 12px rgba(26, 58, 107, 0.25);

      &:hover {
        background: linear-gradient(135deg, #2c5282 0%, #1a3a6b 100%);
        box-shadow: 0 6px 16px rgba(26, 58, 107, 0.35);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }
}

@media (max-width: 768px) {
  .member-type-select {
    padding: 20px 10px;

    .select-card {
      .card-header {
        h2 {
          font-size: 22px;
        }

        p {
          font-size: 14px;
        }
      }

      .action-buttons {
        flex-direction: column;

        .el-button {
          width: 100%;
        }
      }
    }
  }

  .confirm-header {
    .header-logo {
      padding: 24px 0 12px;

      .logo-icon {
        width: 64px;
        height: 64px;
      }
    }

    .confirm-title {
      font-size: 20px;
    }

    .confirm-org {
      font-size: 13px;
    }
  }

  .confirm-body {
    padding: 24px 0 20px;

    .confirm-info-table {
      .table-row {
        flex-direction: column;
        align-items: stretch;

        .table-label {
          flex: none;
          border-right: none;
          border-bottom: 1px solid #e0e6ed;
          padding: 12px 16px;
        }

        .table-value,
        .table-amount {
          padding: 12px 16px;
        }
      }
    }

    .confirm-benefits {
      .benefits-grid {
        grid-template-columns: 1fr;
      }
    }

    .confirm-notice {
      padding: 14px 16px;

      .notice-content {
        .notice-title {
          font-size: 13px;
        }

        .notice-text {
          font-size: 12px;
        }
      }
    }
  }

  .confirm-footer {
    flex-direction: column;
    gap: 12px;

    .footer-btn {
      width: 100%;
    }
  }
}
</style>

<style lang="scss">
/* 确认弹窗样式 - 正式庄重风格 */
.confirm-dialog {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(26, 58, 107, 0.2);

  .el-dialog__header {
    padding: 0;
    margin-right: 0;
  }

  .el-dialog__body {
    padding: 0 40px;
  }

  .el-dialog__footer {
    padding: 20px 40px 32px;
  }

  .el-dialog__headerbtn {
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
    font-size: 20px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 1);
    }

    .el-dialog__close {
      color: #5a6c7d;
      transition: all 0.3s ease;

      &:hover {
        color: #1a3a6b;
      }
    }
  }
}

@media (max-width: 768px) {
  .confirm-dialog {
    width: 92% !important;
    margin: 0 auto;

    .el-dialog__body {
      padding: 0 24px;
    }

    .el-dialog__footer {
      padding: 16px 24px 24px;
    }
  }
}

/* 完善信息弹窗样式 */
.member-info-dialog {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(26, 58, 107, 0.2);

  .el-dialog__header {
    padding: 0;
    margin-right: 0;
  }

  .el-dialog__body {
    padding: 32px 40px;
    max-height: 65vh;
    overflow-y: auto;
    background: linear-gradient(180deg, #fafbfc 0%, #ffffff 100%);
  }

  .el-dialog__footer {
    padding: 0;
    border-top: 1px solid #e8eef5;
  }

  .el-dialog__headerbtn {
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
    font-size: 20px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 1);
    }

    .el-dialog__close {
      color: #5a6c7d;
      transition: all 0.3s ease;

      &:hover {
        color: #1a3a6b;
      }
    }
  }

  .info-dialog-header {
    text-align: center;
    padding: 0;
    background: linear-gradient(180deg, #f8f9fc 0%, #ffffff 100%);
    border-bottom: 2px solid #1a3a6b;
    position: relative;

    .header-logo {
      padding: 28px 0 16px;

      .logo-icon {
        width: 72px;
        height: 72px;
        margin: 0 auto;
        background: linear-gradient(135deg, #1a3a6b 0%, #2c5282 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        box-shadow: 0 4px 16px rgba(26, 58, 107, 0.25);
        border: 3px solid #fff;
        position: relative;

        &::after {
          content: '';
          position: absolute;
          inset: -6px;
          border-radius: 50%;
          border: 1px solid rgba(26, 58, 107, 0.15);
        }
      }
    }

    .info-dialog-title {
      margin: 0 0 8px;
      font-size: 24px;
      font-weight: 700;
      color: #1a3a6b;
      letter-spacing: 2px;
      font-family: 'Microsoft YaHei', 'SimHei', sans-serif;
    }

    .info-dialog-org {
      margin: 0 0 20px;
      font-size: 14px;
      color: #5a6c7d;
      letter-spacing: 3px;
      font-weight: 500;
    }

    .header-divider {
      height: 3px;
      background: linear-gradient(90deg, transparent 0%, #c8a96e 50%, transparent 100%);
      margin: 0 auto;
      width: 60%;
    }
  }

  .info-dialog-footer {
    display: flex;
    justify-content: center;
    gap: 16px;
    padding: 20px 40px 32px;
    background-color: #fafbfc;

    .footer-btn {
      min-width: 140px;
      height: 46px;
      font-size: 15px;
      font-weight: 600;
      letter-spacing: 1px;
      border-radius: 4px;
      transition: all 0.3s ease;

      &.footer-btn--cancel {
        border: 1px solid #d1d5db;
        color: #6b7280;
        background: #ffffff;

        &:hover {
          border-color: #9ca3af;
          color: #4b5563;
          background: #f9fafb;
        }
      }

      &.footer-btn--primary {
        background: linear-gradient(135deg, #1a3a6b 0%, #2c5282 100%);
        border: none;
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(26, 58, 107, 0.25);

        &:hover {
          background: linear-gradient(135deg, #2c5282 0%, #1a3a6b 100%);
          box-shadow: 0 6px 16px rgba(26, 58, 107, 0.35);
          transform: translateY(-1px);
        }

        &:active {
          transform: translateY(0);
        }
      }
    }
  }

  @media (max-width: 768px) {
    width: 92% !important;
    margin: 0 auto;

    .el-dialog__body {
      padding: 24px 24px;
    }

    .info-dialog-header {
      .header-logo {
        padding: 24px 0 12px;

        .logo-icon {
          width: 64px;
          height: 64px;
        }
      }

      .info-dialog-title {
        font-size: 20px;
      }

      .info-dialog-org {
        font-size: 13px;
      }
    }

    .info-dialog-footer {
      flex-direction: column;
      gap: 12px;
      padding: 16px 24px 24px;

      .footer-btn {
        width: 100%;
      }
    }
  }
}

/* 表单样式优化 */
.member-info-form {
  .el-form-item {
    margin-bottom: 24px;

    .el-form-item__label {
      color: #1a3a6b;
      font-weight: 600;
      font-size: 14px;
      line-height: 40px;
    }

    .el-input__wrapper {
      box-shadow: 0 0 0 1px #d0d7de inset;
      border-radius: 6px;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 0 0 1px #1a3a6b inset;
      }

      &.is-focus {
        box-shadow: 0 0 0 1px #1a3a6b inset, 0 0 0 3px rgba(26, 58, 107, 0.1);
      }
    }

    .el-textarea__inner {
      border-color: #d0d7de;
      border-radius: 6px;
      transition: all 0.3s ease;

      &:hover {
        border-color: #1a3a6b;
      }

      &:focus {
        border-color: #1a3a6b;
        box-shadow: 0 0 0 3px rgba(26, 58, 107, 0.1);
      }
    }

    .el-radio-group {
      .el-radio {
        margin-right: 20px;

        .el-radio__label {
          color: #303133;
          font-size: 14px;
        }

        &.is-checked {
          .el-radio__inner {
            background-color: #1a3a6b;
            border-color: #1a3a6b;
          }

          .el-radio__label {
            color: #1a3a6b;
            font-weight: 500;
          }
        }
      }
    }

    .el-checkbox {
      .el-checkbox__label {
        color: #303133;
        font-size: 14px;
      }

      &.is-checked {
        .el-checkbox__inner {
          background-color: #1a3a6b;
          border-color: #1a3a6b;
        }

        .el-checkbox__label {
          color: #1a3a6b;
          font-weight: 500;
        }
      }
    }
  }

  .el-divider {
    margin: 32px 0 24px;
    border-top: 2px solid #e8eef5;

    .el-divider__text {
      background-color: #fff;
      color: #1a3a6b;
      font-weight: 600;
      font-size: 15px;
      letter-spacing: 1px;
      padding: 0 20px;
      position: relative;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 16px;
        background: linear-gradient(180deg, #1a3a6b 0%, #c8a96e 100%);
        border-radius: 2px;
      }
    }
  }
}

/* 上传区域样式 */
.upload-section {
  padding: 24px;
  background: linear-gradient(135deg, #f8f9fc 0%, #fafbfc 100%);
  border: 2px solid #e8eef5;
  border-radius: 12px;
  margin-top: 16px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: 20px;
    right: 20px;
    height: 2px;
    background: linear-gradient(90deg, transparent 0%, #c8a96e 50%, transparent 100%);
  }

  .el-row {
    margin: 0 -10px;
  }

  .el-col {
    padding: 0 10px;
  }

  .el-form-item {
    margin-bottom: 0;

    .el-form-item__label {
      display: none;
    }

    .el-form-item__content {
      margin-left: 0 !important;
    }
  }
}

.upload-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8eef5;
  transition: all 0.3s ease;

  &:hover {
    border-color: #c8a96e;
    box-shadow: 0 4px 12px rgba(200, 169, 110, 0.15);
  }
}

.upload-box {
  width: 140px;
  height: 140px;
  border: 2px dashed #d0d7de;
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(26, 58, 107, 0.05) 0%, rgba(200, 169, 110, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(135deg, #1a3a6b 0%, #c8a96e 100%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: transparent;
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(26, 58, 107, 0.2);

    &::before {
      opacity: 1;
    }

    &::after {
      opacity: 1;
    }
  }

  .upload-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
  }

  .upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #5a6c7d;
    z-index: 1;

    .el-icon {
      color: #1a3a6b;
      font-size: 32px;
      margin-bottom: 8px;
      transition: all 0.3s ease;
    }

    span {
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.5px;
    }
  }

  &:hover .upload-placeholder .el-icon {
    transform: scale(1.1);
    color: #c8a96e;
  }
}

.upload-box--id-photo {
  width: 105px;
  height: 147px;
}

.upload-tip {
  margin-top: 12px;
  color: #5a6c7d;
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
  font-weight: 500;
  padding: 6px 12px;
  background: linear-gradient(135deg, #f0f4f8 0%, #e8eef5 100%);
  border-radius: 6px;
  border: 1px solid #e0e6ed;
}

.experience-item {
  background: #fff;
  padding: 20px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: 2px solid #e8eef5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  &:hover {
    border-color: #c8a96e;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .exp-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid #f0f2f5;

    .exp-title {
      font-size: 15px;
      color: #1a3a6b;
      font-weight: 600;
      letter-spacing: 1px;
      display: flex;
      align-items: center;

      &::before {
        content: '';
        display: inline-block;
        width: 4px;
        height: 16px;
        background: linear-gradient(180deg, #1a3a6b 0%, #c8a96e 100%);
        border-radius: 2px;
        margin-right: 8px;
      }
    }

    .el-button {
      &:hover {
        color: #c8342e;
      }
    }
  }
}

/* 支付弹窗样式 */
.payment-container {
  min-height: 200px;

  &.wechat-active {
    display: flex;
    gap: 30px;
    min-height: 350px;
  }
}

.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 15px;

  .wechat-active & {
    flex: 0 0 200px;
  }

  .payment-item {
    display: flex;
    align-items: center;
    padding: 15px;
    border: 2px solid #e4e7ed;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
    position: relative;

    &:hover {
      border-color: #409eff;
      background-color: #f0f9ff;
    }

    &.active {
      border-color: #409eff;
      background-color: #ecf5ff;
    }

    .payment-icon {
      width: 32px;
      height: 32px;
      margin-right: 12px;
      object-fit: contain;
    }

    .payment-name {
      font-size: 15px;
      color: #303133;
      font-weight: 500;
      flex: 1;
    }

    .check-icon {
      font-size: 18px;
      color: #409eff;
    }
  }

  .alipay-tip {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 12px;
    background: #fdf6ec;
    border: 1px solid #faecd8;
    border-radius: 6px;
    font-size: 13px;
    color: #e6a23c;
    line-height: 1.5;

    .el-icon {
      margin-top: 2px;
      flex-shrink: 0;
    }
  }
}

.qrcode-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 30px;

  .qrcode-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    .payment-logo {
      width: 28px;
      height: 28px;
      margin-right: 10px;
    }

    .payment-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }
  }

  .qrcode-wrapper {
    width: 220px;
    height: 220px;
    background-color: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    padding: 10px;

    .qrcode-canvas {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .qrcode-loading {
      text-align: center;
      color: #909399;

      i {
        font-size: 32px;
        margin-bottom: 10px;
      }

      p {
        margin: 0;
        font-size: 14px;
      }
    }
  }

  .qrcode-tips {
    text-align: center;

    p {
      margin: 0;
      color: #606266;
      font-size: 14px;
      line-height: 1.6;
    }
  }
}
</style>
